"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";

/**
 * Static digit-field backdrop with the σ glyph picked out in brighter digits:
 * the look of the sigmaidentity.com hero, rendered once as a background
 * image. No animation. It draws on mount, on resize, and when the theme
 * flips, and stays transparent so the gradient behind it shows through.
 */

const FONT_SIZE = 9;
const DIGIT_WIDTH = FONT_SIZE * 0.55;
const PAIR_GAP = FONT_SIZE * 1.5;
const ROW_GAP = FONT_SIZE * 1.3;
const EDGE_FADE = 90;
const MAX_DPR = 2;
/** Vertical centre of the glyph as a fraction of the panel height. */
const GLYPH_CENTER_Y = 0.4;
/** How fast the field fades away from the glyph (lower = wider field). */
const RADIAL_FALLOFF = 0.6;
const OKLCH_REGEX = /oklch\(([\d.]+)\s+([\d.]+)\s+([\d.]+)\)/;
interface Palette {
  hue: number;
  saturation: number;
  lightness: number;
  isDark: boolean;
}
function readPalette(): Palette {
  const styles = getComputedStyle(document.documentElement);
  const match = styles.getPropertyValue("--primary").trim().match(OKLCH_REGEX);
  const lightness = match ? Number(match[1]) : 0.6;
  const chroma = match ? Number(match[2]) : 0.1;
  const hue = match ? Number(match[3]) : 232;
  return {
    hue: hue || 0,
    saturation: Math.min(100, chroma * 200),
    lightness: Math.min(100, lightness * 100),
    isDark: document.documentElement.classList.contains("dark")
  };
}
function buildGlyphMask(width: number, height: number): Uint8ClampedArray | null {
  const mask = document.createElement("canvas");
  mask.width = width;
  mask.height = height;
  const context = mask.getContext("2d");
  if (!context) {
    return null;
  }
  const size = Math.floor(Math.min(width * 1.1, height * 0.85));
  context.fillStyle = "white";
  context.font = `italic 700 ${size}px Georgia, STIXGeneral, serif`;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText("σ", width / 2, height * GLYPH_CENTER_Y);
  return context.getImageData(0, 0, width, height).data;
}
function edgeFactor(x: number, y: number, width: number, height: number) {
  const left = Math.min(1, x / EDGE_FADE);
  const right = Math.min(1, (width - x) / EDGE_FADE);
  const top = Math.min(1, y / EDGE_FADE);
  const bottom = Math.min(1, (height - y) / EDGE_FADE);
  return Math.max(0, Math.min(left, right, top, bottom));
}
function digitColor(palette: Palette, radial: number, alpha: number) {
  const lightness = palette.isDark ? Math.min(85, palette.lightness + radial * 18) : Math.max(22, palette.lightness - radial * 26);
  const scaled = Math.min(1, alpha * (palette.isDark ? 0.8 : 0.6));
  return `hsla(${palette.hue}, ${palette.saturation}%, ${lightness}%, ${scaled})`;
}
interface Field {
  context: CanvasRenderingContext2D;
  mask: Uint8ClampedArray;
  palette: Palette;
  width: number;
  height: number;
}

/** One two-digit cell: faint everywhere, brighter inside the σ mask. */
function paintPair(field: Field, x: number, y: number) {
  const {
    context,
    mask,
    palette,
    width,
    height
  } = field;
  const centerX = width / 2;
  const centerY = height * GLYPH_CENTER_Y;
  const distance = Math.hypot(x - centerX, y - centerY);
  const radial = Math.max(0, 1 - distance / Math.hypot(centerX, centerY) * RADIAL_FALLOFF);
  const maskX = Math.floor(Math.min(width - 1, x + DIGIT_WIDTH));
  const inGlyph = (mask[(Math.floor(y) * width + maskX) * 4 + 3] ?? 0) / 255;
  const strength = radial * edgeFactor(x, y, width, height) * (0.7 + inGlyph * 2.4);
  for (let digit = 0; digit < 2; digit += 1) {
    const base = 0.12 + Math.random() * 0.3;
    const alpha = base * strength * (digit === 0 ? 0.7 : 1);
    if (alpha <= 0.015) {
      continue;
    }
    context.fillStyle = digitColor(palette, radial, alpha);
    context.fillText(String(Math.floor(Math.random() * 10)), x + digit * DIGIT_WIDTH, y + FONT_SIZE);
  }
}
function prepareCanvas(canvas: HTMLCanvasElement): Field | null {
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  if (width === 0 || height === 0) {
    return null;
  }
  const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
  canvas.width = Math.floor(width * dpr);
  canvas.height = Math.floor(height * dpr);
  const context = canvas.getContext("2d");
  if (!context) {
    return null;
  }
  context.setTransform(dpr, 0, 0, dpr, 0, 0);
  context.clearRect(0, 0, width, height);
  context.font = `${FONT_SIZE}px "Courier New", Courier, monospace`;
  const mask = buildGlyphMask(width, height);
  if (!mask) {
    return null;
  }
  return {
    context,
    mask,
    palette: readPalette(),
    width,
    height
  };
}
function draw(canvas: HTMLCanvasElement) {
  const field = prepareCanvas(canvas);
  if (!field) {
    return;
  }
  const pairs = Math.ceil(field.width / PAIR_GAP);
  const rows = Math.ceil(field.height / ROW_GAP);
  for (let row = 0; row < rows; row += 1) {
    for (let pair = 0; pair < pairs; pair += 1) {
      paintPair(field, pair * PAIR_GAP, row * ROW_GAP);
    }
  }
}
export function SigmaDigitBackdrop(t0) {
  const $ = _c(4);
  const {
    className
  } = t0;
  const canvasRef = useRef(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }
      const redraw = () => draw(canvas);
      redraw();
      const resize = new ResizeObserver(redraw);
      resize.observe(canvas);
      const theme = new MutationObserver(redraw);
      theme.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class"]
      });
      return () => {
        resize.disconnect();
        theme.disconnect();
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const t3 = `pointer-events-none absolute inset-0 h-full w-full ${className ?? ""}`;
  let t4;
  if ($[2] !== t3) {
    t4 = <canvas aria-hidden={true} className={t3} ref={canvasRef} />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  return t4;
}